import React from 'react'
import { motion } from 'framer-motion'
import { Calendar, MapPin, Clock, Star, Users, Heart, Share2, ExternalLink, CheckCircle } from 'lucide-react'
import { Event } from '../../types'

interface EventCardProps {
  event: Event
  size?: 'small' | 'medium' | 'large'
  showActions?: boolean
}

const EventCard: React.FC<EventCardProps> = ({ event, size = 'medium', showActions = true }) => {
  const [isLiked, setIsLiked] = React.useState(false)
  const [isGoing, setIsGoing] = React.useState(() => {
    try {
      const raw = localStorage.getItem('rsvps')
      const arr = raw ? JSON.parse(raw) : []
      return !!arr.find((x: any) => x.subjectId === event.id && x.subject === 'event')
    } catch { return false }
  })

  const sizeClasses = {
    small: 'max-w-sm',
    medium: 'max-w-md',
    large: 'max-w-lg'
  }

  const imageHeights = {
    small: 'h-40',
    medium: 'h-48',
    large: 'h-64'
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      date: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      time: date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    }
  }

  const eventDate = formatDate(event.startsAt)

  return (
    <motion.div
      className={`glass-card overflow-hidden hover:bg-white/10 transition-all duration-300 cursor-pointer group ${sizeClasses[size]}`}
      whileHover={{ scale: 1.02 }}
      layout
    >
      {event.featured && (
        <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-le-accent to-le-accent-2 text-white text-xs px-3 py-1 rounded-full font-medium">
          Featured
        </div>
      )}

      {event.status === 'live' && (
        <div className="absolute top-4 right-4 z-10 flex items-center space-x-1 bg-red-500 text-white text-xs px-3 py-1 rounded-full font-medium">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span>LIVE</span>
        </div>
      )}

      <div className={`relative ${imageHeights[size]} overflow-hidden`}>
        <img
          src={event.image}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        
        {/* Date Badge */}
        <div className="absolute top-4 left-4 glass-card p-3 text-center min-w-[60px]">
          <div className="text-xs text-white/80">{eventDate.month}</div>
          <div className="text-xl font-bold text-white">{eventDate.date}</div>
          <div className="text-xs text-white/80">{eventDate.day}</div>
        </div>

        {/* Genre Tags */}
        <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
          {event.genres.slice(0, 2).map((genre) => (
            <span
              key={genre}
              className="bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full"
            >
              {genre}
            </span>
          ))}
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-display font-semibold text-xl text-white mb-2 group-hover:text-le-accent transition-colors duration-300 leading-tight">
          {event.title}
        </h3>
        
        <p className="text-white/70 mb-4">{event.artist}</p>

        <div className="space-y-2 text-sm text-white/60 mb-4">
          <div className="flex items-center space-x-2">
            <MapPin size={14} />
            <span>{event.venue}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock size={14} />
            <span>{eventDate.time}</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Star size={14} className="text-yellow-400 fill-current" />
              <span>{event.rating}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Users size={14} />
              <span>{event.attendees} going</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="text-le-accent font-bold text-xl">{event.price}</div>
          {event.ageRestriction && (
            <span className="bg-orange-500/20 text-orange-400 text-xs px-2 py-1 rounded-full">
              {event.ageRestriction}
            </span>
          )}
        </div>

        {showActions && (
          <div className="flex space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsGoing(!isGoing)
                try {
                  const raw = localStorage.getItem('rsvps')
                  const arr = raw ? JSON.parse(raw) : []
                  const exists = arr.find((x: any) => x.subjectId === event.id && x.subject === 'event')
                  if (exists) {
                    localStorage.setItem('rsvps', JSON.stringify(arr.filter((x: any) => !(x.subjectId === event.id && x.subject === 'event'))))
                  } else {
                    arr.push({ subject: 'event', subjectId: event.id, at: new Date().toISOString() })
                    localStorage.setItem('rsvps', JSON.stringify(arr))
                  }
                } catch {}
              }}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                isGoing
                  ? 'bg-le-accent text-white'
                  : 'bg-le-accent/20 text-le-accent hover:bg-le-accent hover:text-white'
              }`}
            >
              {isGoing ? (
                <span className="inline-flex items-center gap-2"><CheckCircle size={16}/> Going</span>
              ) : "I'm Going"}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsLiked(!isLiked)
              }}
              className={`p-2 rounded-lg transition-all duration-200 ${
                isLiked
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-white/10 text-white/60 hover:text-red-400'
              }`}
            >
              <Heart size={18} className={isLiked ? 'fill-current' : ''} />
            </button>
            <button className="p-2 bg-white/10 text-white/60 hover:text-white rounded-lg transition-all duration-200">
              <Share2 size={18} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default EventCard